import React from 'react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Plus } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How do we get started?",
    answer: "Book a free 30 minute call. We'll ask about the problem you're trying to solve, what systems and tools you already use, and what a good result looks like. No preparation needed on your side. Just talk us through it."
  },
  {
    question: "How much does a project typically cost?",
    answer: "Most focused projects start at around NOK 15,000 to 30,000. Larger or more complex builds are scoped and priced individually. We always provide a fixed quote before anything starts. No open invoices, no surprise bills."
  },
  {
    question: "How long does it take to build something?",
    answer: "A focused tool typically takes 2 to 4 weeks from a confirmed brief to a live product. We'll give you a realistic timeline upfront and stick to it."
  },
  {
    question: "Do I own what you build?",
    answer: "Yes, fully. The finished product belongs to you. We can hand over the complete codebase if you want to take it elsewhere, or host and maintain it for you on an ongoing basis. Your choice."
  },
  {
    question: "What if I need changes after launch?",
    answer: "Almost every client wants to adjust something once they start using the tool in real life. We offer ongoing support for updates and new features, either on a monthly retainer or at an hourly rate."
  },
  {
    question: "What exactly is a sales prospecting app?",
    answer: "It's a custom tool built specifically for your sales team. It pulls company data from one or more sources, filters by the criteria that matter to you (industry, size, geography, revenue, technology stack, whatever), and gives your team a clean, prioritized list of companies to reach out to. Instead of paying for a generic database full of irrelevant contacts, you get a tool built around exactly how your team sells."
  },
  {
    question: "Can you integrate with the tools we already use?",
    answer: "Yes. We build integrations with CRM systems, email, calendar tools, ERP systems, accounting software, and most custom databases. If you're using it and it has an API, we can most likely connect to it."
  },
  {
    question: "Do I need to understand the technical side?",
    answer: "Not at all. You explain the business problem, we handle everything technical. At every step we explain what we're building in plain language. No jargon, no technical assumptions."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

export const CommonQuestions = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="py-24 md:py-32 bg-[#0a0a0a] relative">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="container-professional">
        <div className="max-w-2xl mb-14 md:mb-16">
          <div className="text-[11px] font-semibold tracking-[0.2em] text-primary uppercase mb-4">FAQ</div>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-bold text-white tracking-[-0.025em] leading-[1.1]">
            Common questions
          </h2>
        </div>

        <div ref={elementRef} className="max-w-3xl">
          <Accordion type="single" collapsible className="divide-y divide-white/[0.07] border-y border-white/[0.07]" style={getRevealStyle(0)}>
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-none"
              >
                <AccordionTrigger className="group text-left text-[17px] font-medium text-white py-5 hover:no-underline [&>svg]:hidden">
                  <span className="flex-1 pr-6">{faq.question}</span>
                  <Plus
                    className="w-4 h-4 shrink-0 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45"
                    strokeWidth={2}
                  />
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-white/60 leading-relaxed pb-6 pr-10">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
